import styled from 'styled-components';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage, useListNavigationStateContext } from '@/customHooks';
import { capitalizeFirstLetter, propertyTypeMapper } from '@/helpers';
import { PropertyFilterInput, useDeletePropertyMutation, useGetPropertiesQuery } from '@/graphql/generated';
import { ButtonWithDropDownMenu } from '../molecules';
import { PropertyForListing } from '@/custom-types';
import { useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { Col, Row } from 'react-grid-system';
import { LoadingSpinner } from '../atoms';

const textsData = {
  en: {
    name: 'Name',
    location: 'Location',
    owner: 'Owner',
    type: 'Property Type',
    deleteConfirmation: {
      title: 'Property deleted',
      description: 'has been deleted successfully'
    },
    noData: 'Any property ahs been created yet'
  },
  es: {
    name: 'Nombre',
    location: 'Ubicación',
    owner: 'Propietario',
    type: 'Tipo de Propiedad',
    deleteConfirmation: {
      title: 'Propiedad eliminada',
      description: 'ha sido eliminada con éxito'
    },
    noData: 'No ha sido creada ninguna propiedad aún',
  }
};

const StyledGridTable = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  border-radius: 12px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 64px;
`;

const StyledGridHeader = styled.div`
  display: contents;
  & > div {
    font-weight: bold;
    text-align: left;
    padding: 15px;
    border-top: 1px solid grey;
    border-bottom: 1px solid gray;
  }
`;

const StyledGridRow = styled.div`
  display: contents;

  & > div {
    padding: 15px;
    background-color: ${({ theme }) => theme.colors.light};
    font-size: 14px;
    padding-top: 1rem;
    padding-bottom: 1rem;
    align-self: stretch;
    place-content: center;
    border-bottom: 1px solid lightgray;
  }

  &:nth-of-type(odd) > div {
    background-color: ${({ theme }) => theme.colors.background2};
  }
`;

const dataFlatter = (data: PropertyForListing[] | undefined) => {
  if (!data || data.length === 0) return [];

  return data.map((property) => ({
    id: property.id,
    name: property.name,
    city: property.address.city?.name ?? '',
    state: property.address.state?.name ?? '',
    ownerFirstName: property.owner?.firstName ?? '',
    ownerLastName: property.owner?.lastName ?? '',
    propertyTypeOption: property.typeOption,
  }));
};

type PropertyListTableProps = {
  filters: PropertyFilterInput;
};

export const PropertyListTable = ({ filters }: PropertyListTableProps) => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const navigate = useNavigate()
  const { setListState } = useListNavigationStateContext();

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext();

  const { open: errorHandle } = useErrorHandlerContext();

  const { data, error, refetch, loading } = useGetPropertiesQuery({
    variables: {
      input: filters,
    },
    fetchPolicy: 'network-only',
  });
  
  const tableData = dataFlatter(data?.properties?.items);

  const [deleteProperty] = useDeletePropertyMutation();

  const onDeleteHandle = (id: string) => {
    deleteProperty({ variables: { id } })
      .then((response) => {
        confirmDialogModalOpen({
          title: texts.deleteConfirmation.title,
          description: `${response.data?.deleteProperty.name} ${texts.deleteConfirmation.description}`,
        });
        refetch();
      })
      .catch(errorHandle);
  };

  if (loading) {
    return (
      <Row justify='center'>
        <Col xs='content' >
          <LoadingSpinner text/>
        </Col>
      </Row>
    )
  }

  if (tableData.length === 0) {
    return (
      <Row>
        <Col>
          <span><b>{texts.noData}</b></span>
        </Col>
      </Row>
    )
  }

  if (error) errorHandle(error);

  return (
    <StyledGridTable>
      <StyledGridHeader>
        <div>{texts.name}</div>
        <div>{texts.location}</div>
        <div>{texts.owner}</div>
        <div>{texts.type}</div>
        <div></div>
      </StyledGridHeader>

      {tableData.map((data) => (
        <StyledGridRow key={data.id}>
          <div>{capitalizeFirstLetter(data.name)}</div>
          <div>{`${data.city}, ${data.state}`}</div>
          <div>{`${capitalizeFirstLetter(data.ownerFirstName)} ${capitalizeFirstLetter(data.ownerLastName)}`}</div>
          <div>{propertyTypeMapper(data.propertyTypeOption, userLanguage)}</div>
          <div>
            <ButtonWithDropDownMenu
              options={[
                { 
                  item: 'Detalles', 
                  action: () => {
                    setListState('properties', {
                      currentPage: parseInt(filters.page || '1'),
                      filters,
                      selectedItemId: data.id,
                    });
                    navigate(`/${pathURL.USER}/${pathURL.PROPERTY}/${pathURL.DETAIL}`, { state: { id: data.id }});
                  }
                },
                { item: 'Eliminar', action: () => onDeleteHandle(data.id) },
              ]}
              position='left'
            />
          </div>
        </StyledGridRow>
      ))}
    </StyledGridTable>
  );
};
